import React, { useMemo, useState } from "react";
import { WomCard, WomSegmented } from "./design-system/components";

// Fallback emoji scores – used only if record.score is missing
const EMOJI_OPTIONS = [
  { value: "🤩", score: 3 },
  { value: "🥰", score: 2 },
  { value: "🙂", score: 1 },
  { value: "😐", score: 0 },
  { value: "🙁", score: -1 },
  { value: "😢", score: -2 },
  { value: "😭", score: -3 },
];

function getScoreForEmoji(emoji) {
  const found = EMOJI_OPTIONS.find((e) => e.value === emoji);
  return found ? found.score : 0;
}

const tokens = {
  dark: {
    background: "rgba(15,23,42,0.98)",
    backgroundSoft: "rgba(15,23,42,0.95)",
    border: "rgba(148,163,184,0.4)",
    borderSoft: "rgba(148,163,184,0.25)",
    text: "#e2e8f0",
    textSoft: "rgba(148,163,184,0.9)",
    accent: "#38bdf8",
  },
  light: {
    background: "#ffffff",
    backgroundSoft: "#f9fafb",
    border: "rgba(148,163,184,0.45)",
    borderSoft: "rgba(203,213,225,0.8)",
    text: "#0f172a",
    textSoft: "rgba(100,116,139,0.95)",
    accent: "#0284c7",
  },
};

const translations = {
  en: {
    heading: "Mood analytics",
    subheading: "Patterns in how the world feels.",
    totalEntries: "Total entries",
    avgMood: "Average mood",
    globalTemp: "Global mood",
    topEmojis: "Top emojis",
    topContexts: "Top places",
    hourlyTrend: "Hourly trend",
    noData: "No data for this filter yet.",
    period: "Time range",
    range24h: "24 hours",
    range7d: "7 days",
    range30d: "30 days",
    rangeAll: "All time",
    country: "Country",
    city: "City",
    allCountries: "All countries",
    allCities: "All cities",
    contextUnknown: "Other / unknown",
    entries: "entries",
    score: "score",
  },
  uk: {
    heading: "Аналітика настроїв",
    subheading: "Закономірності того, як почувається світ.",
    totalEntries: "Усього записів",
    avgMood: "Середній настрій",
    globalTemp: "Глобальний настрій",
    topEmojis: "Найпопулярніші емодзі",
    topContexts: "Найчастіші місця",
    hourlyTrend: "Погодинна динаміка",
    noData: "Для цього фільтра ще немає даних.",
    period: "Період",
    range24h: "24 години",
    range7d: "7 днів",
    range30d: "30 днів",
    rangeAll: "Завжди",
    country: "Країна",
    city: "Місто",
    allCountries: "Усі країни",
    allCities: "Усі міста",
    contextUnknown: "Інше / невідомо",
    entries: "записів",
    score: "балів",
  },
};

function normalizeTimestampToMs(record) {
  const m = record;
  if (!m) return null;

  const created = m.createdAt;
  if (created) {
    if (typeof created.toDate === "function") {
      return created.toDate().getTime();
    }
    if (typeof created.toMillis === "function") {
      return created.toMillis();
    }
    if (typeof created === "number") {
      // assume ms
      return created;
    }
  }

  if (m.timestamp instanceof Date) {
    return m.timestamp.getTime();
  }
  if (typeof m.timestamp === "number") {
    return m.timestamp;
  }

  return null;
}

function computeAnalytics(moods) {
  if (!moods || moods.length === 0) {
    return {
      avgScore: 0,
      globalTemp: "—",
      total: 0,
      topEmojis: [],
      topContexts: [],
      hourlyTrend: Array.from({ length: 24 }, (_, hour) => ({
        hour,
        value: 0,
      })),
    };
  }

  let totalScore = 0;
  let count = 0;
  const emojiCount = {};
  const contextCount = {};
  const hourly = {};

  moods.forEach((m) => {
    const score =
      typeof m.score === "number"
        ? m.score
        : getScoreForEmoji(m.emoji || m.moodEmoji);

    if (typeof score === "number" && !Number.isNaN(score)) {
      totalScore += score;
      count += 1;
    }

    const emojiKey = m.emoji || m.moodEmoji || "❓";
    emojiCount[emojiKey] = (emojiCount[emojiKey] || 0) + 1;

    const ctxKey = m.context || "unknown";
    contextCount[ctxKey] = (contextCount[ctxKey] || 0) + 1;

    const tsMs = normalizeTimestampToMs(m);
    if (tsMs) {
      const d = new Date(tsMs);
      const h = d.getHours();
      hourly[h] = (hourly[h] || 0) + 1;
    }
  });

  const avgScore = count > 0 ? totalScore / count : 0;

  let globalTemp = "Neutral";
  if (avgScore > 1.5) globalTemp = "Very positive";
  else if (avgScore > 0.5) globalTemp = "Positive";
  else if (avgScore < -1.5) globalTemp = "Very negative";
  else if (avgScore < -0.5) globalTemp = "Negative";

  const topEmojis = Object.entries(emojiCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([emoji, value]) => ({ emoji, value }));

  const topContexts = Object.entries(contextCount)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 5)
    .map(([context, value]) => ({ context, value }));

  const hourlyTrend = Array.from({ length: 24 }, (_, hour) => ({
    hour,
    value: hourly[hour] || 0,
  }));

  return {
    avgScore,
    globalTemp,
    total: moods.length,
    topEmojis,
    topContexts,
    hourlyTrend,
  };
}

export default function AnalyticsPanel({
  moods,
  darkTheme,
  billingPlan = "free", // ⚡ додали план
  language = "en",
}) {
  const t = translations[language] || translations.en;
  const theme = darkTheme ? tokens.dark : tokens.light;

  const [range, setRange] = useState("24h"); // "24h" | "7d" | "30d" | "all"
  const [countryFilter, setCountryFilter] = useState("all"); // "all" or country name
  const [cityFilter, setCityFilter] = useState("all"); // "all" or city name

  // Витягуємо список країн та міст із moods
  const { countries, citiesByCountry } = useMemo(() => {
    if (!Array.isArray(moods)) {
      return { countries: [], citiesByCountry: {} };
    }

    const countrySet = new Set();
    const tempCities = {};

    moods.forEach((m) => {
      const countryRaw = ((m.country || m.countryName || "") + "").trim();
      const cityRaw = ((m.city || m.cityName || "") + "").trim();

      let countryKey = countryRaw;
      if (!countryKey && cityRaw) {
        countryKey = "Unknown";
      }

      if (countryKey) {
        countrySet.add(countryKey);
        if (!tempCities[countryKey]) {
          tempCities[countryKey] = new Set();
        }
        if (cityRaw) {
          tempCities[countryKey].add(cityRaw);
        }
      }
    });

    return {
      countries: Array.from(countrySet).sort((a, b) => a.localeCompare(b)),
      citiesByCountry: Object.fromEntries(
        Object.entries(tempCities).map(([country, set]) => [
          country,
          Array.from(set).sort((a, b) => a.localeCompare(b)),
        ])
      ),
    };
  }, [moods]);

  const availableCities = useMemo(() => {
    if (countryFilter === "all") return [];
    return citiesByCountry[countryFilter] || [];
  }, [citiesByCountry, countryFilter]);

  // ⚡ Опції діапазону за планом
  const baseRangeOptions = [
    { value: "24h", label: t.range24h },
    { value: "7d", label: t.range7d },
  ];

  let rangeOptions = baseRangeOptions;
  if (billingPlan === "business") {
    rangeOptions = [
      ...baseRangeOptions,
      { value: "30d", label: t.range30d },
    ];
  } else if (billingPlan === "pro") {
    rangeOptions = [
      ...baseRangeOptions,
      { value: "30d", label: t.range30d },
      { value: "all", label: t.rangeAll },
    ];
  } else {
    rangeOptions = baseRangeOptions; // free
  }

  const nowMs = Date.now();

  // Фільтр по часу + країні + місту
  const filteredMoods = useMemo(() => {
    if (!Array.isArray(moods)) return [];

    let thresholdMs = null;
    if (range !== "all") {
      if (range === "24h") {
        thresholdMs = nowMs - 24 * 60 * 60 * 1000;
      } else if (range === "7d") {
        thresholdMs = nowMs - 7 * 24 * 60 * 60 * 1000;
      } else if (range === "30d") {
        thresholdMs = nowMs - 30 * 24 * 60 * 60 * 1000;
      }
    }

    return moods.filter((m) => {
      // time filter
      if (thresholdMs !== null) {
        const ts = normalizeTimestampToMs(m);
        if (!ts) return false;
        if (ts < thresholdMs || ts > nowMs) return false;
      }

      // geo filters
      const countryRaw = ((m.country || m.countryName || "") + "").trim();
      const cityRaw = ((m.city || m.cityName || "") + "").trim();
      const countryKey = countryRaw || (cityRaw ? "Unknown" : "");

      if (countryFilter !== "all") {
        if (countryKey !== countryFilter) return false;
      }

      if (cityFilter !== "all") {
        if (cityRaw !== cityFilter) return false;
      }

      return true;
    });
  }, [moods, range, nowMs, countryFilter, cityFilter]);

  const analytics = useMemo(
    () => computeAnalytics(filteredMoods),
    [filteredMoods]
  );

  const { avgScore, globalTemp, total, topEmojis, topContexts, hourlyTrend } =
    analytics;

  const maxHourly = Math.max(...hourlyTrend.map((h) => h.value), 1);
  // компактна версія для маленького графіка – показуємо кожні 2 години
  const compactHourly = hourlyTrend.filter((h) => h.hour % 2 === 0);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        padding: 12,
        boxSizing: "border-box",
        background: theme.backgroundSoft,
      }}
    >
      <WomCard
        padding="md"
        elevated
        style={{
          height: "100%",
          boxSizing: "border-box",
          borderRadius: 18,
          border: `1px solid ${theme.border}`,
          background: theme.background,
          display: "flex",
          flexDirection: "column",
          gap: 12,
          color: theme.text,
        }}
      >
        {/* Header + filters */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div
            style={{
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            {t.heading}
          </div>
          <div
            style={{
              fontSize: 12,
              color: theme.textSoft,
            }}
          >
            {t.subheading}
          </div>

          {/* Filters row */}
          <div
            style={{
              marginTop: 8,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            {/* Time range */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flexWrap: "wrap",
                fontSize: 11,
                color: theme.textSoft,
              }}
            >
              <span
                style={{
                  opacity: 0.9,
                }}
              >
                {t.period}:
              </span>
                <WomSegmented
    value={range}
    onChange={setRange}
    options={rangeOptions}
    size="sm"
    style={{ fontSize: 11 }}
  />
            </div>

            {/* Country / city filters */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flexWrap: "wrap",
                fontSize: 11,
                color: theme.textSoft,
              }}
            >
              {/* Country select */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span>{t.country}:</span>
                <select
                  value={countryFilter}
                  onChange={(e) => {
                    setCountryFilter(e.target.value);
                    setCityFilter("all");
                  }}
                  style={{
  fontSize: 11,
  background: "rgba(15,23,42,0.96)",       // темний фон під наш дизайн
  borderRadius: 999,
  border: `1px solid ${theme.borderSoft}`,
  color: theme.text,
  padding: "4px 10px",
  outline: "none",
  cursor: "pointer",
  appearance: "none",                        // прибирає “системний” стиль там, де можливо
}}

                >
                  <option value="all">{t.allCountries}</option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              {/* City select */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                }}
              >
                <span>{t.city}:</span>
                <select
                  value={cityFilter}
                  onChange={(e) => setCityFilter(e.target.value)}
                  disabled={
                    countryFilter === "all" || availableCities.length === 0
                  }
                  style={{
                    fontSize: 11,
                    background: "transparent",
                    borderRadius: 999,
                    border: `1px solid ${theme.borderSoft}`,
                    color:
                      countryFilter === "all" || availableCities.length === 0
                        ? theme.textSoft
                        : theme.text,
                    padding: "4px 8px",
                    outline: "none",
                    cursor:
                      countryFilter === "all" || availableCities.length === 0
                        ? "default"
                        : "pointer",
                    opacity:
                      countryFilter === "all" || availableCities.length === 0
                        ? 0.6
                        : 1,
                  }}
                >
                  <option value="all">{t.allCities}</option>
                  {availableCities.map((city) => (
                    <option key={city} value={city}>
                      {city}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* No data state */}
        {total === 0 ? (
          <div
            style={{
              flex: 1,
              minHeight: 240,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.textSoft,
              fontSize: 13,
            }}
          >
            {t.noData}
          </div>
        ) : (
          <div
            style={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1.2fr) minmax(0, 1fr)",
              gap: 12,
              minHeight: 260,
            }}
          >
            {/* Left column – key stats + hourly trend */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {/* Key numbers row */}
<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr",   // одна колонка на всю ширину
    gap: 8,
  }}
>
                {/* Total entries */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid ${theme.borderSoft}`,
                    background: theme.backgroundSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.totalEntries}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    {total}
                  </div>
                </div>

                {/* Avg mood */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid ${theme.borderSoft}`,
                    background: theme.backgroundSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.avgMood}
                  </div>
                  <div
                    style={{
                      fontSize: 18,
                      fontWeight: 600,
                    }}
                  >
                    {avgScore.toFixed(2)}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.score}
                  </div>
                </div>

                {/* Global mood */}
                <div
                  style={{
                    borderRadius: 12,
                    padding: 10,
                    border: `1px solid ${theme.borderSoft}`,
                    background: theme.backgroundSoft,
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.globalTemp}
                  </div>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {globalTemp}
                  </div>
                </div>
              </div>

                                          {/* Hourly trend – line chart */}
              <div
                style={{
                  borderRadius: 12,
                  padding: 10,
                  border: `1px solid ${theme.borderSoft}`,
                  background: theme.backgroundSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                  minHeight: 120,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {t.hourlyTrend}
                </div>

                <div
                  style={{
                    marginTop: 4,
                    height: 80,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <svg
                    viewBox="0 0 100 40"
                    preserveAspectRatio="none"
                    style={{
                      width: "100%",
                      height: 60,
                      overflow: "visible",
                    }}
                  >
                    {hourlyTrend.length > 0 && (
                      <>
                        <polyline
                          fill="none"
                          stroke={theme.accent}
                          strokeWidth={1.5}
                          points={hourlyTrend
                            .map((h, index) => {
                              const x =
                                (index /
                                  Math.max(hourlyTrend.length - 1, 1)) * 100;
                              const y =
                                35 -
                                ((h.value / (maxHourly || 1)) * 25);
                              return `${x},${y}`;
                            })
                            .join(" ")}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          opacity={0.9}
                        />
                        {hourlyTrend.map((h, index) => {
                          const x =
                            (index /
                              Math.max(hourlyTrend.length - 1, 1)) * 100;
                          const y =
                            35 -
                            ((h.value / (maxHourly || 1)) * 25);
                          return (
                            <circle
                              key={h.hour}
                              cx={x}
                              cy={y}
                              r={1.4}
                              fill={theme.accent}
                              opacity={h.value === 0 ? 0.3 : 1}
                            />
                          );
                        })}
                      </>
                    )}
                  </svg>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontSize: 9,
                      color: theme.textSoft,
                      paddingTop: 2,
                    }}
                  >
                    {[0, 6, 12, 18].map((h) => (
                      <span key={h}>{h}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right column – top emojis + contexts */}



            {/* Right column – top emojis + contexts */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
              }}
            >
              {/* Top emojis */}
              <div
                style={{
                  borderRadius: 12,
                  padding: 10,
                  border: `1px solid ${theme.borderSoft}`,
                  background: theme.backgroundSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {t.topEmojis}
                </div>
                {topEmojis.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.noData}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {topEmojis.map((item, index) => (
                      <div
                        key={`${item.emoji}-${index}`}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          gap: 8,
                          fontSize: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 18,
                            }}
                          >
                            {item.emoji}
                          </span>
                          <span
                            style={{
                              color: theme.textSoft,
                            }}
                          >
                            {item.value} {t.entries}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Top contexts */}
              <div
                style={{
                  borderRadius: 12,
                  padding: 10,
                  border: `1px solid ${theme.borderSoft}`,
                  background: theme.backgroundSoft,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 500,
                  }}
                >
                  {t.topContexts}
                </div>
                {topContexts.length === 0 ? (
                  <div
                    style={{
                      fontSize: 11,
                      color: theme.textSoft,
                    }}
                  >
                    {t.noData}
                  </div>
                ) : (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 4,
                    }}
                  >
                    {topContexts.map((item, index) => {
                      const label =
                        item.context && item.context !== "unknown"
                          ? item.context
                          : t.contextUnknown;
                      return (
                        <div
                          key={`${item.context}-${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                            fontSize: 12,
                          }}
                        >
                          <span>{label}</span>
                          <span
                            style={{
                              color: theme.textSoft,
                            }}
                          >
                            {item.value} {t.entries}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </WomCard>
    </div>
  );
}
